import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Form, FormDetail } from '@features/forms';
import DeleteIcon from '@mui/icons-material/Delete';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Skeleton,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';

import SlideSidePane from '../../Views/Shared/SlideSidePane';
import BaseBlock from '../Patient/Blocks/BaseBlock';

import FormDetailView from './FormDetailView';

interface FormDetailsSlidePaneProps {
  open: boolean;
  onClose: () => void;
  selectedForm: Form | null;
  formDetail?: FormDetail;
  isLoadingDetail?: boolean;
  subjectLanguage: string;
  onDeleteForm: (formSessionId: string) => Promise<void>;
  allowDelete?: boolean;
}

const formatDate = (timestamp: number): string => {
  // Convert from seconds to milliseconds if needed
  const date = new Date(timestamp * 1000);
  return date.toLocaleString('en-US', {
    month: 'numeric',
    day: 'numeric',
    year: '2-digit',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
};

export const FormDetailsSlidePane: React.FC<FormDetailsSlidePaneProps> = ({
  open,
  onClose,
  selectedForm,
  formDetail,
  isLoadingDetail = false,
  subjectLanguage,
  onDeleteForm,
  allowDelete = true,
}) => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  const handleDeleteForm = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setIsDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setIsDeleteDialogOpen(false);
  };

  const handleConfirmDeleteForm = async () => {
    if (!selectedForm) return;

    try {
      await onDeleteForm(selectedForm.form_session_id);
      setIsDeleteDialogOpen(false);
      onClose();
    } catch (error) {
      console.error('Failed to delete form:', error);
      enqueueSnackbar('Failed to delete form', { variant: 'error' });
    }
  };

  const handleCopyLink = () => {
    if (!formDetail) return;

    const patientFormUrl = `${import.meta.env.VITE_FORMS_URL}/f/${formDetail.form_session_id}`;
    navigator.clipboard
      .writeText(patientFormUrl)
      .then(() => {
        enqueueSnackbar('Link copied to clipboard!', { variant: 'success' });
      })
      .catch((error: unknown) => {
        console.error('Failed to copy:', error);
        enqueueSnackbar('Failed to copy link', { variant: 'error' });
      });
  };

  return (
    <SlideSidePane
      open={open}
      onClose={onClose}
      title={selectedForm?.title || 'Form Details'}
      action={
        selectedForm && allowDelete && (
          <IconButton onClick={handleDeleteForm} size="small" aria-label="delete form">
            <DeleteIcon />
          </IconButton>
        )
      }
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
        }}
      >
        <Dialog
          open={isDeleteDialogOpen}
          onClose={handleCloseDeleteDialog}
          aria-labelledby="delete-dialog-title"
          aria-describedby="delete-dialog-description"
          container={() => document.querySelector('.MuiSlideSidePane-content')}
          sx={{
            '& .MuiDialog-paper': {
              width: '80%',
              maxWidth: 400,
              m: 0,
            },
          }}
        >
          <DialogTitle id="delete-dialog-title">Delete?</DialogTitle>
          <DialogContent>
            <DialogContentText id="delete-dialog-description">
              Are you sure you want to delete this form from this {subjectLanguage}? This action
              cannot be undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDeleteDialog} color="primary">
              Cancel
            </Button>
            <Button onClick={handleConfirmDeleteForm} color="error">
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        {isLoadingDetail ? (
          <Box sx={{ p: 2 }}>
            <Skeleton variant="text" sx={{width: "60%", height: 32,  mb: 1 }} />
            <Skeleton variant="text" sx={{width: "90%", height: 20,  mb: 3 }} />

            <Skeleton
              variant="rectangular"
              sx={{width: "100%", height: 60,  mb: 2, borderRadius: 1 }}
            />

            <Box sx={{ mt: 4, mb: 2 }}>
              <Skeleton variant="text" sx={{width: "40%", height: 24,  mb: 1 }} />
              <Skeleton variant="rectangular" sx={{width: "100%", height: 120,  borderRadius: 1 }} />
            </Box>

            <Box sx={{ mt: 4 }}>
              <Skeleton variant="text" sx={{width: "40%", height: 24,  mb: 1 }} />
              <Skeleton variant="rectangular" sx={{width: "100%", height: 200,  borderRadius: 1 }} />
            </Box>
          </Box>
        ) : selectedForm && formDetail ? (
          <>
            <Box
              sx={{
                p: 2,
                flexGrow: 1,
                overflow: 'auto',
                pb: 9,
              }}
            >
              <Typography variant="h6" gutterBottom>
                {formDetail.form_definition.title}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {formDetail.form_definition.description}
              </Typography>
              <Box sx={{ mt: 3, mb: 3 }}>
                <BaseBlock title="Form Status">
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                    <Chip
                      label={
                        formDetail.status === 'completed'
                          ? 'Completed'
                          : formDetail.status === 'in_progress'
                            ? 'In Progress'
                            : 'Pending'
                      }
                      color={
                        formDetail.status === 'completed'
                          ? 'success'
                          : formDetail.status === 'in_progress'
                            ? 'primary'
                            : 'warning'
                      }
                      size="small"
                      sx={{ mr: 1.5 }}
                    />
                    <Typography variant="body2" color="text.secondary">
                      {formDetail.status === 'completed'
                        ? 'Form has been completed by the patient'
                        : formDetail.status === 'in_progress'
                          ? 'Patient has started but not completed the form'
                          : 'Form has been assigned but not started'}
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: 'flex',
                      position: 'relative',
                      mt: 3,
                      mb: 1,
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: '50%',
                        left: 0,
                        right: 0,
                        height: 4,
                        bgcolor: 'grey.200',
                        transform: 'translateY(-50%)',
                        zIndex: 0,
                        borderRadius: 2,
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        position: 'relative',
                        zIndex: 1,
                        flex: 1,
                      }}
                    >
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: '50%',
                          bgcolor: 'success.main',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white',
                          mb: 1,
                        }}
                      >
                        1
                      </Box>
                      <Typography variant="caption" align="center">
                        Assigned
                      </Typography>
                      <Typography variant="caption" color="text.secondary" align="center">
                        {formDetail.created_timestamp
                          ? formatDate(formDetail.created_timestamp)
                          : '-'}
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        position: 'relative',
                        zIndex: 1,
                        flex: 1,
                      }}
                    >
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: '50%',
                          bgcolor: formDetail.start_timestamp ? 'success.main' : 'grey.400',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white',
                          mb: 1,
                        }}
                      >
                        2
                      </Box>
                      <Typography variant="caption" align="center">
                        Started
                      </Typography>
                      <Typography variant="caption" color="text.secondary" align="center">
                        {formDetail.start_timestamp ? formatDate(formDetail.start_timestamp) : '-'}
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        position: 'relative',
                        zIndex: 1,
                        flex: 1,
                      }}
                    >
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: '50%',
                          bgcolor: formDetail.end_timestamp ? 'success.main' : 'grey.400',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white',
                          mb: 1,
                        }}
                      >
                        3
                      </Box>
                      <Typography variant="caption" align="center">
                        Completed
                      </Typography>
                      <Typography variant="caption" color="text.secondary" align="center">
                        {formDetail.end_timestamp ? formatDate(formDetail.end_timestamp) : '-'}
                      </Typography>
                    </Box>
                  </Box>
                </BaseBlock>
              </Box>

              {formDetail.status === 'completed' && (
                <BaseBlock
                  title="Form Submission"
                  actions={
                    <Button
                      variant="contained"
                      size="small"
                      color="primary"
                      endIcon={<OpenInNewIcon />}
                      onClick={() => {
                        navigate(`/forms/${formDetail.form_session_id}`);
                      }}
                      sx={{
                        borderRadius: '8px',
                        textTransform: 'none',
                        fontSize: '0.85rem',
                        px: 2,
                      }}
                    >
                      Full View
                    </Button>
                  }
                >
                  <FormDetailView
                    formDefinition={formDetail.form_definition}
                    // The legacy forms contract still exposes submission_data as `any`.
                    // Keep that compatibility boundary local to the legacy viewer.
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                    submissionData={formDetail.submission_data}
                    mode="condensed"
                  />
                </BaseBlock>
              )}
            </Box>

            {formDetail.status !== 'completed' && (
              <Box
                sx={{
                  position: 'sticky',
                  bottom: 0,
                  p: 2,
                  bgcolor: 'background.paper',
                  borderTop: 1,
                  borderColor: 'divider',
                  mt: 'auto',
                }}
              >
                <Button variant="contained" fullWidth onClick={handleCopyLink}>
                  Copy Patient Link
                </Button>
              </Box>
            )}
          </>
        ) : (
          <Box sx={{ p: 2 }}>
            <Typography color="text.secondary">No form details available</Typography>
          </Box>
        )}
      </Box>
    </SlideSidePane>
  );
};

export default FormDetailsSlidePane;
